import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { RoleListItem } from "../../Dto/RoleModel";
import { RoleEffects } from "../../State/RoleEffect";
import { RoleState } from "../../State/RoleState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-role-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./RoleHomePage.html",
  styleUrl: "./RoleHomePage.scss",
})
export class RoleHomePage implements OnInit {
  private readonly roleEffects = inject(RoleEffects);
  readonly roleState = inject(RoleState);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);

  roleColumns: TableColumn<RoleListItem>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    { key: "name", header: "Name", sortable: true, width: "18%" },
    { key: "description", header: "Description", sortable: true, width: "25%" },
    {
      key: "isSystem",
      header: "System Role",
      sortable: true,
      width: "10%",
      cellTemplate: (item) =>
        (() => {
          if (item.isSystem) {
            return "Yes";
          }
          return "No";
        })(),
    },
    {
      key: "permissions",
      header: "Permissions",
      sortable: false,
      width: "12%",
      cellTemplate: (item) => item.permissions?.length ?? 0,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "25%",
      cellTemplate: (item) => formatDateNumeric(item.createdAt),
    },
  ];

  get roleConfig(): TableConfig<RoleListItem> {
    return {
      loading: this.roleState.loading(),
      emptyMessage: "No roles found",
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search roles...",
      showCreate: true,
      createButtonLabel: "Create Role",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      onCreate: () => this.createRole(),
      actionButtons: [
        {
          icon: "edit",
          label: "Edit",
          handler: (item: RoleListItem) => this.editRole(item),
          visible: () => true,
        },
        {
          icon: "delete",
          label: "Delete",
          handler: (item: RoleListItem) => this.deleteRole(item),
          visible: () => true,
        },
      ],
    };
  }

  get rolePagination(): TablePagination {
    return this.roleState.pagination();
  }

  ngOnInit(): void {
    this.roleEffects.searchRoles().catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    // Removed logging
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.roleState.searchParams();
    this.roleEffects.searchRoles({ ...currentSearchParams, page: event.page, search: event.search }).catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.roleState.searchParams();
    this.roleEffects
      .searchRoles({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  onSearchChange(event: TableSearchEvent): void {
    this.roleEffects.searchRoles({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.roleState.searchParams();
    this.roleEffects
      .searchRoles({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  createRole(): void {
    this.router.navigate(["/roles/create"]).catch(() => undefined);
  }

  editRole(role: RoleListItem): void {
    this.router.navigate(["/roles/edit", role.id], { state: { role } }).catch(() => undefined);
  }

  deleteRole(role: RoleListItem): void {
    this.confirmModalEffects.open({
      title: "Delete role?",
      message: `This will permanently delete the role "${role.name}". This action cannot be undone.`,
      confirmText: "Delete role",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.roleEffects.deleteRole(role.id);
          this.toastEffects.show("Role deleted", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to delete role"), "error");
        }
      },
    });
  }
}
